/**
 * Logger utility that wraps console methods
 * In production environment, logging is disabled to avoid unnecessary output
 *
 * Works in both Node.js and browser environments
 *
 * Usage:
 *   logger.log('message')                    // Only in development
 *   logger.log('message', { always: true })  // Always show, even in production
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

interface LogOptions {
  always?: boolean
}

interface Logger {
  log: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  debug: (...args: unknown[]) => void
}

const isProduction = (): boolean => {
  // Works in both Node.js and browser (Vite replaces process.env.NODE_ENV at build time)
  try {
    return process.env.NODE_ENV === 'production'
  } catch {
    return false
  }
}

const isLogOptions = (arg: unknown): arg is LogOptions => {
  return typeof arg === 'object' && arg !== null && 'always' in arg
}

const createLogMethod = (level: LogLevel): ((...args: unknown[]) => void) => {
  return (...args: unknown[]): void => {
    // Check if last argument is options with { always: true }
    const lastArg = args[args.length - 1]
    const hasAlwaysOption = isLogOptions(lastArg) && lastArg.always === true

    // Remove options from args if present
    const logArgs = hasAlwaysOption ? args.slice(0, -1) : args

    // Skip logging in production unless always is true
    if (isProduction() && !hasAlwaysOption) {
      return
    }

    console[level](...logArgs)
  }
}

export const logger: Logger = {
  log: createLogMethod('log'),
  info: createLogMethod('info'),
  warn: createLogMethod('warn'),
  error: createLogMethod('error'),
  debug: createLogMethod('debug'),
}

export default logger
